import { Button, Container, Stack, toast } from '@landler/component-library';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { IndicatorFactType } from '@/api/rest/resources/types/fact';
import { PlotGroupComparison } from '@/api/rest/resources/types/organization';
import { DEFAULT_PLOT_GROUP_FILTERS, DEFAULT_PLOT_GROUP_GROUP_BY } from '@/api/rest/resources/types/plotGroup';
import { parsePlotGroupBodyData } from '@/components/PlotGroups/utils/parsePlotGroupBodyData';
import { usePdfExport } from '@/hooks/usePdfExport';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { useOrganizationPlotGroups } from '@/pages/shared/hooks/useOrganizationPlotGroup';
import { isNullish } from '@/utils/booleans';

import { ComingSoon } from '../components/ComingSoon';
import { useIsLimitedAccessUser } from '../hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '../hooks/useIsOrganizationMember';
import { useOrganizationId } from '../hooks/useOrganizationId';
import { useOrganizationPlotGroupComparisons } from '../hooks/useOrganizationPlotGroupComparisons';
import { CompareColumns, CompareFilterFields } from './components/CompareColumns';
import { EditPlotGroupComparison } from './components/EditPlotGroupComparison';
import { ALL_INDICATOR_FACT_TYPES } from './constants';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ComparePage = () => {
  const { t } = useTranslation();

  const containerRef = useRef<HTMLDivElement>(null);
  const { exportToPdf, isExporting } = usePdfExport({
    filename: `${t('navbar.comparisons')}_${new Date().toISOString()}.pdf`,
    orientation: 'landscape',
    onError: () => {
      toast({
        title: t('global.compare.export.toast.errorToastFallbackTitle'),
        description: t('global.compare.export.toast.errorToastFallbackDescription'),
        type: 'error',
      });
    },
  });

  const organizationId = useOrganizationId();
  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();
  const hideComparisons = isLimitedAccessUser || !isOrganizationMember;

  const [searchParams] = useSearchParams();
  const comparisonId = searchParams.get('comparisonId');
  const comparisons = useOrganizationPlotGroupComparisons(organizationId).data;
  const comparison = comparisons?.find((c) => c.id === comparisonId);
  const comparisonLeftGroup = comparison?.plot_groups?.[0] ?? null;
  const comparisonRightGroup = comparison?.plot_groups?.[1] ?? null;

  const [selectedIndicators, setSelectedIndicators] = useState<IndicatorFactType[]>(
    comparison?.indicators ?? ALL_INDICATOR_FACT_TYPES,
  );

  useEffect(() => {
    setSelectedIndicators(comparison?.indicators ?? ALL_INDICATOR_FACT_TYPES);
  }, [comparison?.id, comparison?.indicators]);

  const plotGroupBodies = useOrganizationPlotGroups(organizationId).data;

  const leftForm = useForm<CompareFilterFields>({
    defaultValues: {
      ...DEFAULT_PLOT_GROUP_FILTERS,
      ...DEFAULT_PLOT_GROUP_GROUP_BY,
      groupId: comparisonLeftGroup?.id ?? undefined,
      name: comparisonLeftGroup?.name ?? '',
      additional_plot_ids: comparisonLeftGroup?.additional_plot_ids ?? [],
    },
  });
  const leftGroupId = leftForm.watch('groupId');
  const leftGroupBody = leftGroupId ? plotGroupBodies.find((g) => g.id === leftGroupId) || null : null;
  const leftGroup = leftGroupBody ? parsePlotGroupBodyData(leftGroupBody) : null;

  const rightForm = useForm<CompareFilterFields>({
    defaultValues: {
      ...DEFAULT_PLOT_GROUP_FILTERS,
      ...DEFAULT_PLOT_GROUP_GROUP_BY,
      groupId: comparisonRightGroup?.id ?? undefined,
      name: comparisonRightGroup?.name ?? '',
      additional_plot_ids: comparisonRightGroup?.additional_plot_ids ?? [],
    },
  });
  const rightGroupId = rightForm.watch('groupId');
  const rightGroupBody = rightGroupId ? plotGroupBodies.find((g) => g.id === rightGroupId) || null : null;
  const rightGroup = rightGroupBody ? parsePlotGroupBodyData(rightGroupBody) : null;

  const hideIndicator = (indicator: IndicatorFactType) => {
    setSelectedIndicators((currentIndicators) =>
      currentIndicators.filter((currentIndicator) => currentIndicator !== indicator),
    );
  };

  const plotGroupComparison = {
    ...comparison,
    plot_groups: !isNullish(leftGroup) && !isNullish(rightGroup) ? [leftGroup, rightGroup] : [],
    indicators: selectedIndicators,
  } as unknown as PlotGroupComparison;

  return (
    <SignedInPageLayout
      items={[]}
      withCustomContentContainer
      navBarProps={{
        title: comparison?.name ?? t('navbar.comparisons'),
        showFilters: false,
        children: !hideComparisons ? (
          <Stack direction='row' className='justify-end gap-4'>
            <EditPlotGroupComparison
              disabled={plotGroupComparison.plot_groups ? plotGroupComparison.plot_groups.length < 2 : true}
              organizationId={organizationId}
              plotGroupComparison={plotGroupComparison}
              selectedIndicators={selectedIndicators}
              onIndicatorsChange={setSelectedIndicators}
            />
            <Button
              type='button'
              disabled={plotGroupComparison.plot_groups ? plotGroupComparison.plot_groups.length < 2 : true}
              loading={isExporting}
              variant='outline'
              onClick={() => exportToPdf(containerRef.current)}
            >
              {t('navbar.actionButtons.dashboard')}
            </Button>
          </Stack>
        ) : null,
      }}
    >
      <Container ref={containerRef} gutterWidth={{ sm: 2 }} className='gap-10 my-8 ' data-testid='compare-list-page'>
        {!hideComparisons ? (
          <CompareColumns
            leftForm={leftForm}
            rightForm={rightForm}
            leftGroup={leftGroup}
            rightGroup={rightGroup}
            indicators={selectedIndicators}
            onHideIndicator={hideIndicator}
          />
        ) : (
          <ComingSoon />
        )}
      </Container>
    </SignedInPageLayout>
  );
};
